/** Statuses where the backend stops the SLA clock, so the UI must not keep ticking. */
const TERMINAL_SLA_STATUS_CODES = new Set([
  'closed',
  'resolved',
  'pending_supervisor_approval',
  'supervisor_approved',
]);

/** Stable status codes where the backend sets sla_paused_at (display names may change). */
const SLA_PAUSED_STATUS_CODE_MARKERS = ['pending_customer'];

const normalizeStatusToken = (value) =>
  String(value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');

function findStatusMaster(statuses, ticket) {
  if (!Array.isArray(statuses) || statuses.length === 0 || !ticket) {
    return null;
  }
  if (ticket.statusId != null) {
    const byId = statuses.find(
      (s) => String(s.statusId ?? s.id) === String(ticket.statusId)
    );
    if (byId) return byId;
  }
  const label = ticket.statusName || ticket.status;
  if (!label || typeof label !== 'string') {
    return null;
  }
  const wanted = normalizeStatusToken(label);
  return (
    statuses.find(
      (s) =>
        normalizeStatusToken(s.statusCode || s.code) === wanted
        || normalizeStatusToken(s.statusName || s.name) === wanted
    ) || null
  );
}

/**
 * Resolve stable ticket_statuses.status_code for SLA logic.
 * Prefer statusCode on the ticket; fall back to statusId + master rows; last resort normalize status string.
 */
export function resolveTicketStatusCode(ticketOrStatus, statuses) {
  if (ticketOrStatus == null) return '';
  if (typeof ticketOrStatus === 'string' || typeof ticketOrStatus === 'number') {
    return normalizeStatusToken(ticketOrStatus);
  }

  const ticket = ticketOrStatus;
  const direct = normalizeStatusToken(
    ticket.statusCode ?? ticket.status?.statusCode ?? ticket.status_code
  );
  if (direct) return direct;

  const master = findStatusMaster(statuses, ticket);
  if (master) {
    return normalizeStatusToken(master.statusCode || master.code);
  }

  const rawStatus = ticket.status;
  if (typeof rawStatus === 'string') {
    return normalizeStatusToken(rawStatus);
  }
  if (rawStatus && typeof rawStatus === 'object') {
    return normalizeStatusToken(rawStatus.statusCode || rawStatus.statusName);
  }
  return '';
}

export const isTerminalSlaStatus = (ticketOrStatus, statuses) =>
  TERMINAL_SLA_STATUS_CODES.has(resolveTicketStatusCode(ticketOrStatus, statuses));

/**
 * Pending-customer statuses set `sla_paused_at` server-side, so the backend remaining/overdue
 * values are frozen at the pause instant and the UI must not tick past them.
 */
export const isSlaPausedStatus = (ticketOrStatus, statuses) => {
  const code = resolveTicketStatusCode(ticketOrStatus, statuses);
  if (!code) return false;
  return SLA_PAUSED_STATUS_CODE_MARKERS.some(
    (marker) => code === marker || code.includes(marker)
  );
};

/** True when the backend has stopped the clock (terminal status or pending-customer pause). */
export const isSlaClockFrozen = (ticketOrStatus, statuses) =>
  isTerminalSlaStatus(ticketOrStatus, statuses) || isSlaPausedStatus(ticketOrStatus, statuses);

/**
 * Live SLA countdown for the CURRENT escalation level's window.
 *
 * Seeded from the backend remaining/overdue values (which are computed against the current
 * level's due date and already exclude pending-customer pause time), then ticked forward
 * locally so the UI updates every second without another API call.
 *
 * `isBreached` / `isEscalated` are deliberately ignored: they describe ticket history, so a
 * ticket auto-escalated from a breached Level 1 keeps them true while its new Level 2 window
 * still has time left. Only the current window decides remaining vs overdue.
 *
 * @param slaStatus backend SLA status ({ remainingMinutes, remainingSeconds, overdueMinutes, overdueSeconds })
 * @param tickSeconds whole seconds elapsed since slaStatus was fetched
 * @param options.slaUsesBusinessHours when true, seeds are the latest backend business-SLA
 *        values; tickSeconds is whole seconds since that response (smooth UI until next poll).
 * @returns { remainingMinutes, remainingSeconds, overdueMinutes, overdueSeconds } where the
 *          overdue pair is null while time remains
 */
export const computeSlaCountdown = (slaStatus, tickSeconds, options = {}) => {
  const seededRemaining =
    (slaStatus?.remainingMinutes ?? 0) * 60 + (slaStatus?.remainingSeconds ?? 0);
  const seededOverdue =
    (slaStatus?.overdueMinutes ?? 0) * 60 + (slaStatus?.overdueSeconds ?? 0);
  const tick = Math.max(0, tickSeconds ?? 0);

  // D1: interpolate from the last /sla-status seed; baseline resets only when caller passes a new seed.
  if (options.slaUsesBusinessHours) {
    if (seededOverdue > 0) {
      const overdue = seededOverdue + tick;
      return {
        remainingMinutes: 0,
        remainingSeconds: 0,
        overdueMinutes: Math.floor(overdue / 60),
        overdueSeconds: overdue % 60,
      };
    }
    const remaining = Math.max(0, seededRemaining - tick);
    if (remaining > 0) {
      return {
        remainingMinutes: Math.floor(remaining / 60),
        remainingSeconds: remaining % 60,
        overdueMinutes: null,
        overdueSeconds: null,
      };
    }
    return {
      remainingMinutes: 0,
      remainingSeconds: 0,
      overdueMinutes: null,
      overdueSeconds: null,
    };
  }

  const remaining = seededRemaining - seededOverdue - tick;

  if (remaining > 0) {
    return {
      remainingMinutes: Math.floor(remaining / 60),
      remainingSeconds: remaining % 60,
      overdueMinutes: null,
      overdueSeconds: null,
    };
  }

  const overdue = -remaining;
  return {
    remainingMinutes: 0,
    remainingSeconds: 0,
    overdueMinutes: Math.floor(overdue / 60),
    overdueSeconds: overdue % 60,
  };
};

/**
 * Elapsed minutes for SLA panel: D1 uses backend business elapsed; others use wall clock when active.
 */
export const resolveDisplayElapsedMinutes = ({
  effectiveSla,
  isTerminal,
  isPaused,
  assignedTime,
  slaUsesBusinessHours,
  nowMs = Date.now(),
}) => {
  let elapsedMinutes = effectiveSla?.elapsedMinutes ?? null;
  if (!isTerminal && !isPaused && assignedTime && !slaUsesBusinessHours) {
    const assigned = new Date(assignedTime);
    elapsedMinutes = Math.floor((nowMs - assigned.getTime()) / (1000 * 60));
  }
  return elapsedMinutes;
};

export default computeSlaCountdown;
